// Copyright 2026, University of Maryland, All Rights Reserved
import { useEffect, useMemo, useRef, useState, type FC } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, BookOpen, Bot, FileText, Loader2, Lock, Settings, Sparkles, Trash2, Workflow } from 'lucide-react'

import {
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@/components/ui'
import { toast } from 'sonner'
import { useAuth } from '@/subsystems/workspaces'
import { useServices } from '@/app-shell/services'
import { useAgentKnowledgeBases, useUpdateAgent, useDeleteAgent, usePromoteAgent, usePromotePreview, extractResourceConflicts, conflictsBySource, type Agent, type AgentKnowledgeBase, type ResourceConflict, type UpdateAgentPayload } from '@/subsystems/agents'
import { ConfirmDialog } from '@/components/forms/confirm-dialog'
import { Field, FormDialog, IdMapSection } from '@/components/forms'
import { useAppConfig } from '@/applications/shared'
import { useAgentDatasets, useUpdateDatasetUserSections } from '@/subsystems/pipelines'
import type { DatasetRecord } from '@/subsystems/pipelines/types'
import { KnowledgeBaseUserViewWrapper } from '@/subsystems/knowledge-base/shared/components/UserViewWrapper'
import { KnowledgeBaseAdminView } from '@/subsystems/knowledge-base/shared/components/AdminView'
import { RenderersBuilder } from '@/app-shell/components/renderers/RenderersBuilder'
import { getAllLayoutComponents, getLayoutComponent } from '@/core/layout-registry'
import { cn } from '@/lib/utils'
import { AgentDocumentMappingEditor } from '@/subsystems/agents/components/AgentDocumentMappingEditor'
import { AgentSlotEditor } from '@/subsystems/agents/components/AgentSlotEditor'

// cm-coding: Radix Select cannot represent null as an item value. Map this UI
// sentinel to API null so choosing “None” explicitly clears the dashboard;
// leaving dashboardId out of a PATCH continues to mean “preserve it.”
const NONE_VALUE = '__none__'

const AgentsPage: FC = () => {
  const { agentId = '' } = useParams<{ agentId: string }>()
  const navigate = useNavigate()
  const { isGlobalAdmin, isWorkspaceAdmin, selectedOrganizationId, selectedWorkspaceId } = useAuth()
  const canEditAgent = isGlobalAdmin || isWorkspaceAdmin
  const services = useServices()

  const orgId = selectedOrganizationId ?? ''
  const workspaceId = selectedWorkspaceId ?? ''

  const { agents, slotsEnabled, isLoading } = useAppConfig(services.getClient(), orgId, workspaceId)

  const agent = useMemo(
    () => agents.find(item => item.agentId === agentId),
    [agents, agentId],
  )
  const { data: agentKnowledgeBases = [], isLoading: knowledgeBasesLoading } = useAgentKnowledgeBases(
    services.getClient(), orgId, workspaceId, agentId, Boolean(agent),
  )

  // When the selected agent isn't in the current workspace (e.g. after switching
  // to a workspace that doesn't have it), fall back to the first available agent,
  // or the dashboard when the workspace has no agents. Without this the page spins
  // forever waiting for an agent that will never appear.
  //
  // The redirect is deferred a tick and mutates window.location.hash directly:
  // on a workspace switch, Layout's URL-sync effect runs in the same flush as this
  // one (child effects fire first) and navigates with its stale captured location,
  // clobbering any redirect made here synchronously — the same race RouteGuard
  // works around. After the deferred hash change, Layout's sync effect re-runs
  // against the new location and restores the org/ws search params.
  useEffect(() => {
    if (isLoading || agent) return
    const fallback = agents[0]
    console.log(`[AgentsPage] Agent ${agentId} not in workspace — falling back to ${fallback?.agentId ?? 'dashboard'}`)
    const timer = setTimeout(() => {
      window.location.hash = fallback ? `/agents/${fallback.agentId}` : '/'
    }, 0)
    return () => clearTimeout(timer)
  }, [isLoading, agents, agent, agentId])

  if (!orgId || !workspaceId) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">
        Select a workspace to view agents.
      </div>
    )
  }

  if (isLoading || !agent || knowledgeBasesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-screen-2xl space-y-4 px-6 py-6">
      <Button variant="ghost" size="sm" className="-ml-2 text-muted-foreground" onClick={() => navigate('/')}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Contract Review
      </Button>

      <section className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <div className="flex items-center gap-4 px-8 py-7">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-muted">
            <Bot className="h-6 w-6 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-semibold">{agent.label}</h1>
            <p className="truncate font-mono text-sm text-muted-foreground">{agent.agentId}</p>
          </div>
        </div>

      <Tabs defaultValue={agent.dashboardId ? 'dashboard' : agentKnowledgeBases.length > 0 ? 'knowledge-base' : 'prompts'} className="w-full">
        <TabsList className="h-auto w-full justify-start rounded-none border-b bg-transparent px-8 py-0">
          {agent.dashboardId && <TabsTrigger value="dashboard" className="rounded-none border-b-2 border-transparent px-4 py-4 text-muted-foreground data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground">Dashboard</TabsTrigger>}
          {agentKnowledgeBases.length > 0 && <TabsTrigger value="knowledge-base" className="rounded-none border-b-2 border-transparent px-4 py-4 text-muted-foreground data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground">Knowledge Base</TabsTrigger>}
          <TabsTrigger value="prompts" className="rounded-none border-b-2 border-transparent px-4 py-4 text-muted-foreground data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground">Prompts</TabsTrigger>
          {isGlobalAdmin && <TabsTrigger value="renderers" className="rounded-none border-b-2 border-transparent px-4 py-4 text-muted-foreground data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground">Renderers</TabsTrigger>}
          <TabsTrigger value="settings" className="rounded-none border-b-2 border-transparent px-4 py-4 text-muted-foreground data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground">Settings</TabsTrigger>
        </TabsList>

        {agent.dashboardId && (
          <TabsContent value="dashboard" className="m-0 bg-muted/20 p-8">
            <AgentDashboardTab agent={agent} orgId={orgId} workspaceId={workspaceId} />
          </TabsContent>
        )}

        {agentKnowledgeBases.length > 0 && (
          <TabsContent value="knowledge-base" className="m-0 bg-muted/20 p-8">
            <AgentKnowledgeBases
              knowledgeBases={agentKnowledgeBases}
              orgId={orgId}
              workspaceId={workspaceId}
              isGlobalAdmin={isGlobalAdmin}
            />
          </TabsContent>
        )}

        <TabsContent value="prompts" className="m-0 bg-muted/20 p-8">
          <AgentPrompts agentId={agent.agentId} orgId={orgId} workspaceId={workspaceId} canEdit={canEditAgent} />
        </TabsContent>

        {isGlobalAdmin && (
          <TabsContent value="renderers" className="m-0 bg-muted/20 p-8">
            <div className="rounded-xl border border-border bg-card shadow-sm">
              <RenderersBuilder
                agent={agent}
                organizationId={orgId}
                workspaceId={workspaceId}
              />
            </div>
          </TabsContent>
        )}

          <TabsContent value="settings" className="m-0 bg-muted/20 p-8">
            <AgentSettings
              agent={agent}
              orgId={orgId}
              workspaceId={workspaceId}
              slotsEnabled={slotsEnabled}
              canEdit={canEditAgent}
              onDeleted={() => navigate('/', { replace: true })}
            />
          </TabsContent>
      </Tabs>
      </section>
    </div>
  )
}

const AgentDashboardTab: FC<{ agent: Agent; orgId: string; workspaceId: string }> = ({ agent, orgId, workspaceId }) => {
  const registered = agent.dashboardId ? getLayoutComponent(agent.dashboardId) : undefined
  if (!registered) {
    return <p className="text-sm text-muted-foreground">Dashboard "{agent.dashboardId}" is not registered.</p>
  }
  const DashboardComponent = registered.Component
  return <DashboardComponent key={agent.agentId} orgId={orgId} workspaceId={workspaceId} agentId={agent.agentId} />
}

interface AgentKnowledgeBasesProps {
  knowledgeBases: AgentKnowledgeBase[]
  orgId: string
  workspaceId: string
  isGlobalAdmin: boolean
}

export const AgentKnowledgeBases: FC<AgentKnowledgeBasesProps> = ({
  knowledgeBases,
  orgId,
  workspaceId,
  isGlobalAdmin,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null)

  if (knowledgeBases.length === 1) {
    const kb = knowledgeBases[0]
    return (
      <KnowledgeBasePanel
        instanceId={kb.instanceId}
        label={kb.label ?? kb.instanceId}
        orgId={orgId}
        workspaceId={workspaceId}
        isGlobalAdmin={isGlobalAdmin}
      />
    )
  }

  if (selectedId) {
    const kb = knowledgeBases.find(item => item.instanceId === selectedId)
    return (
      <div className="space-y-3">
        <Button variant="outline" size="sm" onClick={() => setSelectedId(null)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to knowledge bases
        </Button>
        <KnowledgeBasePanel
          instanceId={selectedId}
          label={kb?.label ?? selectedId}
          orgId={orgId}
          workspaceId={workspaceId}
          isGlobalAdmin={isGlobalAdmin}
        />
      </div>
    )
  }

  return (
    <div className="space-y-3 rounded-lg border border-border bg-white p-4">
      <div>
        <h3 className="text-base font-semibold">Knowledge Bases</h3>
        <p className="text-sm text-muted-foreground">
          Select a knowledge base to view or manage its items.
        </p>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-3">
        {knowledgeBases.map(kb => {
          const updated = kb.updatedAt ? new Date(kb.updatedAt) : null
          const updatedLabel = updated && !Number.isNaN(updated.getTime()) ? updated.toLocaleDateString() : null
          return (
            <button
              key={kb.instanceId}
              type="button"
              onClick={() => setSelectedId(kb.instanceId)}
              className="text-left rounded-lg border border-border bg-card p-4 hover:border-primary hover:shadow-sm transition focus:outline-none focus:ring-2 focus:ring-ring flex flex-col"
            >
              <div className="font-medium truncate">{kb.label ?? kb.instanceId}</div>
              <div className="text-xs font-mono text-muted-foreground truncate" title={kb.instanceId}>{kb.instanceId}</div>
              {kb.description && (
                <p className="mt-1 text-sm text-muted-foreground line-clamp-3">{kb.description}</p>
              )}
              <div className="mt-2 text-2xl font-semibold">{kb.itemCount}</div>
              <div className="text-xs text-muted-foreground">
                {kb.itemCount === 1 ? 'item' : 'items'}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="rounded-full border border-border px-2 py-0.5 capitalize">
                  {kb.mode}
                </span>
                {updatedLabel && <span>Updated {updatedLabel}</span>}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

interface KnowledgeBasePanelProps {
  instanceId: string
  label: string
  orgId: string
  workspaceId: string
  isGlobalAdmin: boolean
}

const KnowledgeBasePanel: FC<KnowledgeBasePanelProps> = ({
  instanceId,
  label,
  orgId,
  workspaceId,
  isGlobalAdmin,
}) => {
  const [view, setView] = useState<'user' | 'admin'>('user')
  const kbProps = {
    orgId,
    workspaceId,
    nodeDefinition: 'knowledge_base',
    nodeInstanceId: instanceId,
    label,
  }

  return (
    <div className="space-y-5 rounded-xl border border-border bg-card p-6 shadow-sm">
      <div className="flex items-center justify-between gap-4 rounded-xl border border-border p-5">
        <div className="flex min-w-0 items-center gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <BookOpen className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="truncate font-semibold">{label}</h3>
            <div className="truncate font-mono text-xs text-muted-foreground" title={instanceId}>
              {instanceId}
            </div>
          </div>
        </div>
        {isGlobalAdmin && (
          view === 'admin' ? (
            <Button variant="outline" size="sm" onClick={() => setView('user')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Knowledge Base
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setView('admin')}>
              <Settings className="h-4 w-4 mr-2" />
              Configure
            </Button>
          )
        )}
      </div>
      {view === 'admin' ? (
        <KbAdminPanel {...kbProps} />
      ) : (
        <KnowledgeBaseUserViewWrapper {...kbProps} showHeader={false} />
      )}
    </div>
  )
}

interface AgentPromptsProps {
  agentId: string
  orgId: string
  workspaceId: string
  canEdit: boolean
}

// {{ section_key }} placeholders in a dataset's system prompt are filled in here;
// the same syntax is rendered server-side at analysis time (dataset_template.py).
const PLACEHOLDER_RE = /\{\{\s*(\w+)\s*}}/g
const EDITABLE_SECTION_ONLY_RE = /^\s*\{\{\s*\w+\s*}}\s*$/

const extractPlaceholders = (template: string): string[] => {
  const seen = new Set<string>()
  for (const match of template.matchAll(PLACEHOLDER_RE)) seen.add(match[1])
  return [...seen]
}

const renderPrompt = (template: string, sections: Record<string, string>): string =>
  template.replace(PLACEHOLDER_RE, (_full, key: string) => sections[key] ?? '')

const AgentPrompts: FC<AgentPromptsProps> = ({ agentId, orgId, workspaceId, canEdit }) => {
  const { datasets, loading } = useAgentDatasets(orgId, workspaceId, agentId)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (datasets.length === 0) {
    return <p className="text-sm text-muted-foreground">This agent has no datasets.</p>
  }

  return (
    <div className="space-y-4">
      {datasets.map(dataset => (
        <DatasetPromptEditor
          key={dataset.key}
          dataset={dataset}
          agentId={agentId}
          orgId={orgId}
          workspaceId={workspaceId}
          canEdit={canEdit}
        />
      ))}
    </div>
  )
}

interface DatasetPromptEditorProps {
  dataset: DatasetRecord
  agentId: string
  orgId: string
  workspaceId: string
  canEdit: boolean
}

const DatasetPromptEditor: FC<DatasetPromptEditorProps> = ({ dataset, agentId, orgId, workspaceId, canEdit }) => {
  const navigate = useNavigate()
  const { isGlobalAdmin } = useAuth()
  const updateSections = useUpdateDatasetUserSections(orgId, workspaceId, agentId)
  const template = dataset.systemPrompt ?? ''
  const placeholders = useMemo(() => extractPlaceholders(template), [template])
  const showPromptPreview = Boolean(template) && !EDITABLE_SECTION_ONLY_RE.test(template)

  const initial = useMemo<Record<string, string>>(
    () => Object.fromEntries(placeholders.map(key => [key, dataset.userSections?.[key] ?? ''])),
    [placeholders, dataset.userSections],
  )
  const [sections, setSections] = useState(initial)
  useEffect(() => setSections(initial), [initial])

  const dirty = placeholders.some(key => sections[key] !== initial[key])

  const handleSave = () => {
    updateSections.mutate(
      { datasetKey: dataset.key, userSections: sections },
      {
        onSuccess: () => toast.success('Prompt sections saved'),
        onError: (err) =>
          toast.error('Save failed', {
            description: err instanceof Error ? err.message : 'Unknown error',
          }),
      },
    )
  }

  return (
    <section className="space-y-5 rounded-xl border border-border bg-card p-6 shadow-sm">
      <div className="flex items-center gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
          <FileText className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-semibold">{dataset.label}</h3>
          <p className="truncate font-mono text-xs text-muted-foreground">{dataset.key}</p>
        </div>
        {!canEdit && (
          <span className="rounded-full bg-muted px-3 py-1 text-[11px] font-semibold tracking-wide text-muted-foreground">
            READ-ONLY
          </span>
        )}
      </div>

      {(showPromptPreview || !template || isGlobalAdmin) && <div className="space-y-2">
        {showPromptPreview ? (
          <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-muted/30 p-4 font-sans text-sm leading-relaxed text-foreground">
            {renderPrompt(template, sections)}
          </pre>
        ) : !template ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
            No prompt set for this dataset.
          </div>
        ) : null}
        {isGlobalAdmin && (
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() => navigate(`/datasets?agent=${agentId}&dataset=${dataset.key}`)}
          >
            Edit dataset template
          </Button>
        )}
      </div>}

      {placeholders.length === 0 ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Lock className="h-4 w-4" />
          This prompt has no editable sections.
        </p>
      ) : (
        <>
          <div className="space-y-3">
            {placeholders.map(key => (
              <div key={key} className="space-y-1">
                <Label htmlFor={`section-${dataset.key}-${key}`} className="font-mono text-xs">
                  {key}
                </Label>
                <Textarea
                  id={`section-${dataset.key}-${key}`}
                  value={sections[key] ?? ''}
                  onChange={(e) => setSections(prev => ({ ...prev, [key]: e.target.value }))}
                  disabled={!canEdit || updateSections.isPending}
                  rows={3}
                />
              </div>
            ))}
          </div>

          {canEdit && <div className="flex justify-end">
            <Button onClick={handleSave} disabled={!dirty || updateSections.isPending}>
              {updateSections.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>}
        </>
      )}
    </section>
  )
}

interface AgentSettingsProps {
  agent: Agent
  orgId: string
  workspaceId: string
  slotsEnabled: boolean
  onDeleted: () => void
  canEdit?: boolean
  hidePromote?: boolean
  /** Override where the pipeline edit button goes (templates switch to the source workspace first). */
  onEditPipeline?: () => void
}

export const AgentSettings: FC<AgentSettingsProps> = ({ agent, orgId, workspaceId, slotsEnabled, onDeleted, canEdit = true, hidePromote, onEditPipeline }) => {
  const services = useServices()
  const navigate = useNavigate()
  const { isGlobalAdmin: isGlobalAdmin } = useAuth()
  const updateAgent = useUpdateAgent(services.getClient(), orgId, workspaceId)
  const deleteAgent = useDeleteAgent(services.getClient(), orgId, workspaceId)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [deleteKbs, setDeleteKbs] = useState(false)

  const handleDelete = () => {
    deleteAgent.mutate({ agentId: agent.agentId, deleteKnowledgeBases: deleteKbs }, {
      onSuccess: () => {
        setConfirmDeleteOpen(false)
        onDeleted()
      },
      onError: (err) =>
        toast.error('Delete failed', {
          description: err instanceof Error ? err.message : 'Unknown error',
        }),
    })
  }

  const [labelDraft, setLabelDraft] = useState(agent.label)
  useEffect(() => setLabelDraft(agent.label), [agent.label])

  const [descriptionDraft, setDescriptionDraft] = useState(agent.description ?? '')
  useEffect(() => setDescriptionDraft(agent.description ?? ''), [agent.description])

  const persist = (next: UpdateAgentPayload) =>
    updateAgent.mutate(
      { agentId: agent.agentId, payload: next },
      {
        onError: (err) =>
          toast.error('Update failed', {
            description: err instanceof Error ? err.message : 'Unknown error',
          }),
      },
    )

  const handleLabelBlur = () => {
    const next = labelDraft.trim()
    if (!next || next === agent.label) {
      setLabelDraft(agent.label)
      return
    }
    persist({ label: next })
  }

  const handleDescriptionBlur = () => {
    const next = descriptionDraft.trim()
    const current = agent.description ?? ''
    if (next === current) return
    persist({ description: next || null })
  }

  const dashboardOptions = useMemo(
    () => getAllLayoutComponents().filter((component) => component.display.category === 'agent-dashboard'),
    [],
  )

  return (
    <div className="max-w-4xl space-y-6 rounded-xl border border-border bg-card p-6 shadow-sm">
      <header className="flex items-center gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
          <Settings className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold">Agent Settings</h3>
          <p className="truncate font-mono text-xs text-muted-foreground">{agent.agentId}</p>
        </div>
        {!canEdit && (
          <span className="rounded-full bg-muted px-3 py-1 text-[11px] font-semibold tracking-wide text-muted-foreground">
            READ-ONLY
          </span>
        )}
      </header>

      <section className="max-w-2xl space-y-2">
        <Label htmlFor="agent-label">Label</Label>
        <Input
          id="agent-label"
          value={labelDraft}
          onChange={(e) => setLabelDraft(e.target.value)}
          onBlur={handleLabelBlur}
          disabled={!canEdit || updateAgent.isPending}
        />
      </section>

      <section className="max-w-2xl space-y-2">
        <Label htmlFor="agent-description">Description</Label>
        <Textarea
          id="agent-description"
          value={descriptionDraft}
          onChange={(e) => setDescriptionDraft(e.target.value)}
          onBlur={handleDescriptionBlur}
          disabled={!isGlobalAdmin || updateAgent.isPending}
          placeholder="What this agent does"
          rows={2}
        />
      </section>

      {!canEdit && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Lock className="h-4 w-4" />
          Managed by a workspace administrator — read-only for your role.
        </p>
      )}

      <AgentDocumentMappingEditor
        api={services.getClient()}
        orgId={orgId}
        workspaceId={workspaceId}
        value={agent.documentMappings}
        disabled={!canEdit}
        saving={updateAgent.isPending}
        onSave={documentMappings => persist({ documentMappings })}
      />

      {slotsEnabled && (
        <AgentSlotEditor
          slots={agent.slots}
          allowAdditionalSlots={agent.allowAdditionalSlots}
          disabled={!canEdit || updateAgent.isPending}
          onSlotsChange={slots => persist({ slots })}
          onAllowAdditionalSlotsChange={allowAdditionalSlots => persist({ allowAdditionalSlots })}
        />
      )}

      {isGlobalAdmin && (
      <>
      <section className="space-y-2 max-w-2xl">
        <Label>Pipeline</Label>
        <div className="flex items-center gap-3">
          <span className="text-sm font-mono text-muted-foreground">
            {agent.pipelineId ?? 'None'}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={onEditPipeline ?? (() => navigate(`/pipelines?agent=${agent.agentId}`))}
          >
            <Workflow className="h-4 w-4 mr-2" />
            {agent.pipelineId ? 'Edit Pipeline' : 'Create Pipeline'}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          The agent owns its pipeline; changes apply only to this agent.
        </p>
      </section>

      <section className="space-y-2 max-w-2xl">
        <Label>Dashboard</Label>
        <Select
          value={agent.dashboardId ?? NONE_VALUE}
          onValueChange={(value) => persist({ dashboardId: value === NONE_VALUE ? null : value })}
          disabled={updateAgent.isPending}
        >
          <SelectTrigger><SelectValue placeholder="Select dashboard" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_VALUE}>None</SelectItem>
            {dashboardOptions.map((option) => (
              <SelectItem key={option.id} value={option.id}>{option.display.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </section>

      <section className="space-y-2 max-w-2xl">
        <Label>Document Filter</Label>
        <div className="flex items-center gap-3">
          <Switch
            id="agent-require-multiple-versions"
            checked={(agent.documentFilter?.minVersions ?? 1) > 1}
            onCheckedChange={(value) =>
              persist({
                documentFilter: {
                  ...agent.documentFilter,
                  minVersions: value ? 2 : 1,
                },
              })
            }
            disabled={updateAgent.isPending}
          />
          <Label htmlFor="agent-require-multiple-versions" className="cursor-pointer">
            Require multiple versions (only show documents with ≥ 2 versions)
          </Label>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            id="agent-docx-only"
            checked={(agent.documentFilter?.extensions ?? []).includes('.docx')}
            onCheckedChange={(value) =>
              persist({
                documentFilter: {
                  ...agent.documentFilter,
                  extensions: value ? ['.docx'] : undefined,
                },
              })
            }
            disabled={updateAgent.isPending}
          />
          <Label htmlFor="agent-docx-only" className="cursor-pointer">
            Only .docx files
          </Label>
        </div>
      </section>

      {!hidePromote && (
        <PromoteToTemplateSection agent={agent} orgId={orgId} workspaceId={workspaceId} />
      )}

      <section className="space-y-2 max-w-2xl border-t pt-6">
        <p className="text-sm text-muted-foreground">
          Deleting this agent removes everything it owns: its configuration,
          pipeline, and datasets. This cannot be undone.
        </p>
        <Button
          variant="destructive"
          onClick={() => { setDeleteKbs(false); setConfirmDeleteOpen(true) }}
          disabled={deleteAgent.isPending}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete Agent
        </Button>
      </section>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={`Delete agent "${agent.label}"?`}
        description="This permanently removes the agent, its pipeline, and its datasets. This cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        loading={deleteAgent.isPending}
        onConfirm={handleDelete}
      >
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <Checkbox
            checked={deleteKbs}
            onCheckedChange={value => setDeleteKbs(value === true)}
            className="mt-0.5"
          />
          <span>
            Also delete its knowledge bases
            <span className="block text-xs text-muted-foreground">
              Knowledge bases are shared within the workspace — leave unchecked
              if other agents use them.
            </span>
          </span>
        </label>
      </ConfirmDialog>
      </>
      )}
    </div>
  )
}

interface PromoteToTemplateSectionProps {
  agent: Agent
  orgId: string
  workspaceId: string
}

const PromoteToTemplateSection: FC<PromoteToTemplateSectionProps> = ({
  agent,
  orgId,
  workspaceId,
}) => {
  const services = useServices()
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const promote = usePromoteAgent(services.getClient(), orgId, workspaceId)
  const { data: preview } = usePromotePreview(
    services.getClient(), orgId, workspaceId, agent.agentId, open,
  )

  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')
  const [agentId, setAgentId] = useState('')
  const [pipelineId, setPipelineId] = useState('')
  const [kbIdMap, setKbIdMap] = useState<Record<string, string>>({})
  const [rendererIdMap, setRendererIdMap] = useState<Record<string, string>>({})
  const [conflicts, setConflicts] = useState<ResourceConflict[]>([])

  // Reset on open/close AND on agent change while open, so no target IDs
  // leak from a previously promoted agent into this payload.
  useEffect(() => {
    setPipelineId('')
    setKbIdMap({})
    setRendererIdMap({})
    setConflicts([])
    if (!open) {
      setLabel('')
      setDescription('')
      setAgentId('')
    } else {
      setLabel(agent.label)
      setDescription(agent.description ?? '')
      setAgentId(agent.agentId)
    }
  }, [open, agent.label, agent.description, agent.agentId])

  // Prefill each target ID with its source ID as a sensible default; the user
  // can override, and the conflict check flags any that already exist. Maps are
  // rebuilt strictly from the preview's IDs (keeping user edits for IDs still
  // present) so entries from a stale preview never survive.
  useEffect(() => {
    if (!open || !preview) return
    setPipelineId(prev => prev || (preview.pipelineId ?? ''))
    setKbIdMap(prev =>
      Object.fromEntries(preview.kbInstanceIds.map(id => [id, prev[id] ?? id])),
    )
    setRendererIdMap(prev =>
      Object.fromEntries(preview.rendererIds.map(id => [id, prev[id] ?? id])),
    )
  }, [open, preview])

  const agentIdError = conflicts.find(c => c.field === 'agentId')?.message
  const errorsBySource = (field: ResourceConflict['field']) => conflictsBySource(conflicts, field)

  const allFilled = (m: Record<string, string>) =>
    Object.values(m).every(v => v.trim().length > 0)

  const canSubmit =
    !!preview &&
    label.trim().length > 0 &&
    agentId.trim().length > 0 &&
    (preview.pipelineId === null || pipelineId.trim().length > 0) &&
    allFilled(kbIdMap) &&
    allFilled(rendererIdMap)

  const handleSubmit = async () => {
    if (!canSubmit || !preview) return
    setSubmitting(true)
    setConflicts([])
    try {
      await promote.mutateAsync({
        agentId: agent.agentId,
        payload: {
          agentId: agentId.trim(),
          label: label.trim(),
          description: description.trim() || null,
          pipelineId: preview.pipelineId ? pipelineId.trim() : null,
          kbIdMap,
          rendererIdMap,
        },
      })
      toast.success('Template created', {
        description: `"${label.trim()}" has been added to the agent library.`,
      })
      setOpen(false)
    } catch (err) {
      const found = extractResourceConflicts(err)
      if (found) {
        setConflicts(found)
        toast.error('Some IDs already exist', {
          description: 'Update the highlighted fields and try again.',
        })
      } else {
        toast.error('Failed to create template', {
          description: err instanceof Error ? err.message : 'Unknown error',
        })
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="space-y-2 max-w-2xl border-t pt-6">
      <p className="text-sm text-muted-foreground">
        Publish this agent to the application's library so other workspaces can instantiate it.
      </p>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <Sparkles className="h-4 w-4 mr-2" />
        Promote to Template
      </Button>
      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title="Promote to Template"
        onSubmit={handleSubmit}
        submitting={submitting}
        canSubmit={canSubmit}
        submitLabel="Create Template"
      >
        <p className="text-sm text-muted-foreground">
          Specify the IDs to use for the new template and each of its resources in the
          application library. All fields are required.
        </p>
        <Field label="Template Label" htmlFor="promote-template-label">
          <Input
            id="promote-template-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Contract Reviewer"
            autoFocus
          />
        </Field>
        <Field label="Description" htmlFor="promote-template-description">
          <Textarea
            id="promote-template-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What this template does"
            rows={2}
          />
        </Field>
        <Field label="Template Agent ID" htmlFor="promote-template-agent-id" error={agentIdError}>
          <Input
            id="promote-template-agent-id"
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            placeholder="contract-reviewer"
            className={cn(agentIdError && 'border-destructive')}
          />
        </Field>
        {preview?.pipelineId && (
          <Field label="Pipeline ID" htmlFor="promote-pipeline-id">
            <Input
              id="promote-pipeline-id"
              value={pipelineId}
              onChange={(e) => setPipelineId(e.target.value)}
              placeholder={preview.pipelineId}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Source pipeline: <span className="font-mono">{preview.pipelineId}</span>
            </p>
          </Field>
        )}
        {preview && preview.kbInstanceIds.length > 0 && (
          <IdMapSection
            title="Knowledge Bases"
            sources={preview.kbInstanceIds}
            values={kbIdMap}
            onChange={setKbIdMap}
            idPrefix="promote-kb"
            errors={errorsBySource('kb')}
          />
        )}
        {preview && preview.rendererIds.length > 0 && (
          <IdMapSection
            title="Renderers"
            sources={preview.rendererIds}
            values={rendererIdMap}
            onChange={setRendererIdMap}
            idPrefix="promote-renderer"
          />
        )}
      </FormDialog>
    </section>
  )
}

interface KbAdminPanelProps {
  orgId: string
  workspaceId: string
  nodeDefinition: string
  nodeInstanceId: string
  label: string
}

const KbAdminPanel: FC<KbAdminPanelProps> = (props) => {
  const saveRef = useRef<(() => Promise<boolean>) | null>(null)
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!saveRef.current) return
    setSaving(true)
    try {
      await saveRef.current()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <KnowledgeBaseAdminView
        {...props}
        registerSave={(fn) => {
          saveRef.current = fn
        }}
      />
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving…
            </>
          ) : (
            'Save'
          )}
        </Button>
      </div>
    </div>
  )
}

export default AgentsPage
